<script setup>
// 홈 — 모바일. 라우터의 / 가 768px 미만일 때 이 화면이 뜬다.
// Figma xsS20cl9Iwm2XO7WPQkg54 / Mobile 2004:2936 (375 x 6777)
//
// 데스크톱 홈(HomeDesktopView.vue)은 스크롤을 가로채 칸을 넘기는 구조지만
// 모바일은 그냥 아래로 스크롤한다. 그래서 화면을 따로 둔다.
//
// 본문(HomeBodyMobile.vue)은 Figma 좌표 그대로 절대배치된 생성물이라
// 375 캔버스를 뷰포트 가로폭에 맞춰 등비 스케일한다.
// 폭은 언제나 화면을 가득 채운다. 요즘 폰은 대부분 375보다 넓어서(아이폰 16 393,
// Pro Max 430) 확대를 막으면 오른쪽에 흰 띠가 남는다. 768 이상은 데스크톱 화면이 맡는다.
//
// 캔버스 높이 6720 = Figma 6777 − 아이폰 상태바 57.
// 본문은 96(공용 상단 = 시세바 40 + 헤더 56)에서 시작한다.
import { onBeforeUnmount, onMounted, ref } from 'vue'
import SiteHeaderMobile from '@/components/common/SiteHeaderMobile.vue'
import HomeBodyMobile from '@/components/home/HomeBodyMobile.vue'
import { MOBILE_FOOTER_WIDTH, applySiteFooter } from '@/composables/useSiteFooter.js'
import {
  HERO_WHEEL_EASING,
  HERO_WHEEL_END_DEG,
  HERO_WHEEL_RISE_MS,
  HERO_WHEEL_SPIN_MS,
  HERO_WHEEL_START_DEG,
  INTRO_LAST_FRAME,
  INTRO_START_DELAY_MS,
  TRANSITION_MS,
  introHoldAfterFrame,
} from '@/data/homeMotionTiming.js'

const DESIGN_WIDTH = 375
const DESIGN_HEIGHT = 6720

// ── 히어로 인트로 ────────────────────────────────────────────────────
//
// 데스크톱과 같은 연출을 같은 타이밍으로 돌린다(홈1-1 ~ 홈1-15).
//   1 검정 → 2 글로우 → 3 공용 상단 → 4 텍스트 → 5 카드 휠 진입 → 6~15 한 칸씩 10번
//
// 본문은 Figma 생성물이라 손대지 않는다. data-node-id 로 집어 스타일만 씌운다.
// 노드 ID 는 Figma 원본 값이라 본문을 다시 뽑아도 그대로다.
const HERO = '2004:2993' // 검정 판 375x812
const WHEEL_HOLDER = '2004:2994' // 카드 26장을 담은 껍데기 (display:contents)
const GLOWS = ['2004:3024', '2004:3025']
// 제목 → 부제 두 줄. 뒤 숫자는 등장 지연(데스크톱과 같은 간격).
// 눈썹(2004:3082 "하이엔드 실물자산 플랫폼")은 2026-09-23 에 뺐다 — build-screen.mjs 의 DROP_NODES.
const TEXTS = [
  ['2004:3089', 100], // Real Money is Real Gold
  ['2004:3083', 220], // 금과 은을 통해 …
  ['2004:3084', 300], // 시간이 지나도 …
]

// 카드 휠의 회전축과 반지름. 퍼블리싱의 카드 6장 중심을 재서 역산한 값이다.
// (데스크톱은 960,1644 / 모바일은 히어로 바닥에서 281px 아래)
const WHEEL_PIVOT = '188.8px 1093.4px'
// 아래에서 올라오는 거리. 데스크톱은 히어로 1009 에 557 이라 그 비율을 모바일 812 에 맞췄다.
const WHEEL_ENTER_SHIFT = 448
// 글로우가 따라 올라오는 거리도 같은 비율(데스크톱 120).
const GLOW_SHIFT = 96
// 텍스트는 역커튼 대신 살짝 떠오르며 나타난다.
// 세 개에 -translate-x-1/2 가 걸려 있어 인라인 transform 을 주면 가운데 정렬이 날아간다.
// CSS translate 는 별도 속성이라 클래스의 transform 과 겹쳐 적용된다.
const TEXT_SHIFT = 18

// 푸터 메뉴 줄 2004:3403 / 2004:3407 (세 개씩 두 줄)
const FOOTER_ROWS = ['2004:3403', '2004:3407']

const viewport = ref(null)
const canvas = ref(null)
const scale = ref(1)
const headerOn = ref(false)
let observer = null
let introTimer = null
let wheel = null // 카드를 옮겨 담은 상자

const applyScale = () => {
  const width = viewport.value?.clientWidth
  if (!width) return
  const next = width / DESIGN_WIDTH
  if (Math.abs(next - scale.value) < 0.0001) return
  scale.value = next
}

const q = (id) => canvas.value?.querySelector(`[data-node-id="${id}"]`)

// 카드만 따로 돌리려면 감쌀 상자가 필요하다.
// 퍼블리싱의 묶음은 display:contents 라 박스가 없어 transform 이 걸리지 않는다.
// 히어로와 똑같은 크기(375x812)의 상자를 만들어 카드 26장을 옮긴다 —
// 카드들의 calc(50% + …) 가 같은 기준을 보므로 위치는 하나도 바뀌지 않는다.
const mountWheel = () => {
  const holder = q(WHEEL_HOLDER)
  if (!holder || !holder.parentNode) return null
  const box = document.createElement('div')
  box.style.cssText =
    `position:absolute;left:0;top:0;width:${DESIGN_WIDTH}px;height:812px;` +
    `transform-origin:${WHEEL_PIVOT};will-change:transform,opacity`
  holder.parentNode.insertBefore(box, holder)
  while (holder.firstChild) box.appendChild(holder.firstChild)
  return box
}

const paint = (frame) => {
  // 글로우 — 홈1-2
  const on = frame >= 2
  GLOWS.forEach((id) => {
    const el = q(id)
    if (!el) return
    el.style.opacity = on ? '1' : '0'
    el.style.translate = on ? '0 0' : `0 ${GLOW_SHIFT}px`
    el.style.transitionProperty = 'opacity, translate'
    el.style.transitionDuration = `${TRANSITION_MS}ms`
  })

  // 공용 상단 — 홈1-3
  headerOn.value = frame >= 3

  // 텍스트 — 홈1-4
  const textOn = frame >= 4
  TEXTS.forEach(([id, delay]) => {
    const el = q(id)
    if (!el) return
    el.style.opacity = textOn ? '1' : '0'
    el.style.translate = textOn ? '0 0' : `0 ${TEXT_SHIFT}px`
    el.style.transitionProperty = 'opacity, translate'
    el.style.transitionDuration = `${TRANSITION_MS}ms`
    el.style.transitionDelay = textOn ? `${delay}ms` : '0ms'
  })

  // 카드 휠 — 홈1-5. 돌면서 올라와 아우라 자리에서 멈춘다.
  if (!wheel) return
  const wheelIn = frame >= 5
  wheel.style.opacity = frame >= 4 ? '1' : '0'
  wheel.style.translate = wheelIn ? '0 0' : `0 ${WHEEL_ENTER_SHIFT}px`
  wheel.style.rotate = `${wheelIn ? HERO_WHEEL_END_DEG : HERO_WHEEL_START_DEG}deg`
  wheel.style.transitionProperty = 'translate, rotate, opacity'
  wheel.style.transitionDuration = `${HERO_WHEEL_RISE_MS}ms, ${HERO_WHEEL_SPIN_MS}ms, ${TRANSITION_MS}ms`
  wheel.style.transitionTimingFunction = HERO_WHEEL_EASING
}

// 프레임을 하나씩 넘긴다. 스크롤은 막지 않는다 — 내려가도 그대로 재생된다.
const playIntro = () => {
  const advance = (frame) => {
    paint(frame)
    if (frame >= INTRO_LAST_FRAME) return
    introTimer = setTimeout(() => advance(frame + 1), introHoldAfterFrame(frame))
  }
  paint(1)
  introTimer = setTimeout(() => advance(2), INTRO_START_DELAY_MS)
}

onMounted(() => {
  applyScale()
  observer = new ResizeObserver(applyScale)
  observer.observe(viewport.value)

  // 하단 메뉴를 상단 메뉴와 같게 맞춘다. 퍼블리싱은 세 개씩 두 줄이라 상자가 둘이다.
  applySiteFooter(canvas.value, FOOTER_ROWS, { width: MOBILE_FOOTER_WIDTH })

  wheel = mountWheel()
  // 윈도우의 "애니메이션 효과" 끄기와 무관하게 항상 재생한다 — 어느 PC 에서 열어도
  // 같은 화면이어야 한다는 결정이다. 예전에는 그 설정이 켜진 PC 에서 인트로가 통째로
  // 생략돼 마지막 장면만 덩그러니 떠 있었다.
  playIntro()
})

onBeforeUnmount(() => {
  observer?.disconnect()
  observer = null
  clearTimeout(introTimer)
  introTimer = null
  wheel = null
})
</script>

<template>
  <div
    ref="viewport"
    class="w-full overflow-hidden bg-white"
    :style="{ height: `${DESIGN_HEIGHT * scale}px` }"
  >
    <div
      ref="canvas"
      class="relative origin-top-left bg-white"
      :style="{
        width: `${DESIGN_WIDTH}px`,
        height: `${DESIGN_HEIGHT}px`,
        transform: `scale(${scale})`,
      }"
    >
      <div class="absolute left-0 top-0 w-[375px]" :style="{ height: `${DESIGN_HEIGHT}px` }">
        <HomeBodyMobile />
      </div>

      <!-- 공용 상단. 인트로 홈1-3 에서 위에서 내려온다(데스크톱과 같은 시점). -->
      <div
        class="absolute left-0 top-0 z-20 w-[375px] will-change-transform"
        :style="{
          transform: headerOn ? 'translateY(0)' : 'translateY(-100%)',
          opacity: headerOn ? 1 : 0,
          transitionProperty: 'transform, opacity',
          transitionDuration: '700ms',
          transitionTimingFunction: 'cubic-bezier(0.33, 1, 0.68, 1)',
        }"
      >
        <SiteHeaderMobile active="about" />
      </div>
    </div>
  </div>
</template>
